/**
 * Standing copy for the Value Analysis page (page 04), keyed by product type.
 *
 * These are the defaults. Anything the agent has typed into the proposal
 * (proposalData.strengths, .challenges, .buyer_pool, .qualifications,
 * .recommendations) wins over the default for that block - see resolveValueAnalysisCopy
 * below. That keeps the existing per-proposal editing in CRUDProposal working while
 * giving every deck sensible standing text when those fields are left blank.
 */

const RETAIL = {
    strengths: [],
    challenges: [],
    buyerPool: [
        'Local, regional, national, & international private',
        'Capital buyers, many of whom are in 1031 Exchanges',
        'Institutional capital targeting quality, stabilized real estate',
    ],
    // Fixed copy for every multi-tenant property - not agent-editable. {transactions} is
    // substituted with the deck's total closed count (page 05's "total number of all
    // properties sold"), so the two figures can never disagree. ValueAnalysis renders it
    // as a link to the matching properties list.
    qualifications: [
        'STRIVE: We are the most active team in Texas & surrounding states.',
        'Track Record: We have closed {transactions} transactions to date.',
        'STRIVE Team controls the entire process: marketing department, financial consulting department, transaction coordinators, A designated leader over every property vertical with over 150 years of combined experience, and a team of experienced agents to ensure high probability of closing.',
        '100% corporation with outside agents and agents within STRIVE, which leads to more buyers, more offers and higher prices.',
        'Ability to source qualified debt through Athena Capital',
    ],
    recommendations: [
        'Sell now to take advantage of the current market before interest rates increase further and capitalize on 1031 buyers',
        'If awarded the Assignment, we can be on the market within one week and closed within our average List-to-Close time frame of 5-6 months',
    ],
};

export const VALUE_ANALYSIS_COPY = {
    Retail: RETAIL,
};

/**
 * Split an agent-entered free text block into bullet lines. Agents type these as
 * newline or leading-dash separated lists.
 */
export function toLines(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return String(value)
        .split(/\r?\n/)
        .map(line => line.replace(/^\s*[-•]\s*/, '').trim())
        .filter(Boolean);
}

/**
 * @param productType  keys into VALUE_ANALYSIS_COPY
 * @param proposalData agent-entered copy; wins over the defaults where present
 *
 * The Qualifications lines keep their {transactions} token: it is substituted by
 * ValueAnalysis, which renders the count as a link and therefore needs markup rather
 * than a string. See TRANSACTIONS_TOKEN there.
 */
export function resolveValueAnalysisCopy(productType, proposalData = {}) {
    const defaults = VALUE_ANALYSIS_COPY[productType] || VALUE_ANALYSIS_COPY.Retail;
    const pick = (entered, fallback) => {
        const lines = toLines(entered);
        return lines.length ? lines : fallback;
    };

    return {
        strengths: pick(proposalData.strengths, defaults.strengths),
        challenges: pick(proposalData.challenges, defaults.challenges),
        buyerPool: pick(proposalData.buyer_pool, defaults.buyerPool),
        // Fixed for every multi-tenant property, so proposalData is deliberately ignored
        // here - unlike the blocks above, an agent cannot override it.
        qualifications: defaults.qualifications,
        recommendations: pick(proposalData.recommendations, defaults.recommendations),
    };
}
